import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as workOrdersApi from '../api/workOrders';

export function WorkOrderDetail() {
  const { workOrderId } = useParams();
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canReadAssets || !workOrderId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await workOrdersApi.getWorkOrder(selectedOrg.id, workOrderId);
      if (!data) {
        setError('Work order not found');
      } else {
        setWorkOrder(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, workOrderId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return { bg: '#c6f6d5', color: '#276749' };
      case 'in_progress': return { bg: '#bee3f8', color: '#2b6cb0' };
      case 'open': return { bg: '#fefcbf', color: '#975a16' };
      case 'cancelled': return { bg: '#fed7d7', color: '#c53030' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return { bg: '#fed7d7', color: '#c53030' };
      case 'medium': return { bg: '#fefcbf', color: '#975a16' };
      case 'low': return { bg: '#c6f6d5', color: '#276749' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view work orders.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading work order...</div>
      </div>
    );
  }

  if (error || !workOrder) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error || 'Work order not found'}</div>
        <button onClick={() => navigate('/service/work-orders')} style={styles.backButton}>
          ← Back to Work Orders
        </button>
      </div>
    );
  }

  const statusStyle = getStatusColor(workOrder.status);
  const priorityStyle = getPriorityColor(workOrder.priority);

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/service/work-orders')} style={styles.backLink}>
        ← Back to Work Orders
      </button>

      <PageHeader
        title={workOrder.title}
        subtitle={`Work Order ${workOrder.id}`}
      />

      <div style={styles.headerMeta}>
        <span style={{ ...styles.badge, background: statusStyle.bg, color: statusStyle.color }}>
          {workOrder.status.replace('_', ' ')}
        </span>
        <span style={{ ...styles.badge, background: priorityStyle.bg, color: priorityStyle.color }}>
          {workOrder.priority} priority
        </span>
        <span style={styles.typeBadge}>{workOrder.type}</span>
      </div>

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('details')}
          style={activeTab === 'details' ? styles.activeTab : styles.tab}
          data-testid="details-tab"
        >
          Details
        </button>
        <button
          onClick={() => setActiveTab('parts')}
          style={activeTab === 'parts' ? styles.activeTab : styles.tab}
          data-testid="parts-tab"
        >
          Parts ({workOrder.parts.length})
        </button>
        <button
          onClick={() => setActiveTab('photos')}
          style={activeTab === 'photos' ? styles.activeTab : styles.tab}
          data-testid="photos-tab"
        >
          Photos ({workOrder.photos.length})
        </button>
        <button
          onClick={() => setActiveTab('timeline')}
          style={activeTab === 'timeline' ? styles.activeTab : styles.tab}
          data-testid="timeline-tab"
        >
          Timeline
        </button>
        <button
          onClick={() => setActiveTab('notes')}
          style={activeTab === 'notes' ? styles.activeTab : styles.tab}
          data-testid="notes-tab"
        >
          Notes ({workOrder.notes.length})
        </button>
      </div>

      {activeTab === 'details' && (
        <div style={styles.content} data-testid="details-content">
          <div style={styles.grid}>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>Location</h4>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Site</span>
                <span style={styles.fieldValue}>{workOrder.siteName}</span>
              </div>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Asset</span>
                <span style={styles.fieldValue}>{workOrder.assetName}</span>
              </div>
            </div>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>Assignment</h4>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Assigned To</span>
                <span style={styles.fieldValue}>{workOrder.assignedTo || 'Unassigned'}</span>
              </div>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Technician</span>
                <span style={styles.fieldValue}>{workOrder.assignedTechName || '-'}</span>
              </div>
            </div>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>Cost Summary</h4>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Labor Hours</span>
                <span style={styles.fieldValue}>{workOrder.laborHours} hrs</span>
              </div>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Total Cost</span>
                <span style={styles.fieldValue}>${workOrder.totalCost.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <h4 style={styles.cardTitle}>Description</h4>
            <p style={styles.description}>{workOrder.description}</p>
          </div>

          <div style={styles.card}>
            <h4 style={styles.cardTitle}>Scope</h4>
            <p style={styles.description}>{workOrder.scope}</p>
          </div>

          {workOrder.rootCause && (
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>Root Cause</h4>
              <p style={styles.description}>{workOrder.rootCause}</p>
            </div>
          )}

          {workOrder.resolution && (
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>Resolution</h4>
              <p style={styles.description}>{workOrder.resolution}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'parts' && (
        <div style={styles.content} data-testid="parts-content">
          {workOrder.parts.length === 0 ? (
            <div style={styles.emptyState}>No parts recorded</div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Part Number</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Quantity</th>
                    <th style={styles.th}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrder.parts.map((part, idx) => (
                    <tr key={idx} style={styles.tr}>
                      <td style={styles.td}><code>{part.partNumber}</code></td>
                      <td style={styles.td}>{part.name}</td>
                      <td style={styles.td}>{part.quantity}</td>
                      <td style={styles.td}>${part.cost.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'photos' && (
        <div style={styles.content} data-testid="photos-content">
          {workOrder.photos.length === 0 ? (
            <div style={styles.emptyState}>No photos attached</div>
          ) : (
            <div style={styles.photosGrid}>
              {workOrder.photos.map(photo => (
                <div key={photo.id} style={styles.photoCard}>
                  <div style={styles.photoPlaceholder}>📷</div>
                  <div style={styles.photoInfo}>
                    <span style={styles.photoFilename}>{photo.filename}</span>
                    <span style={styles.photoCaption}>{photo.caption}</span>
                    <span style={styles.photoDate}>{new Date(photo.uploadedAt).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'timeline' && (
        <div style={styles.content} data-testid="timeline-content">
          <div style={styles.timeline}>
            {workOrder.timeline.map((event, idx) => (
              <div key={idx} style={styles.timelineItem}>
                <div style={styles.timelineDot} />
                <div style={styles.timelineContent}>
                  <span style={styles.timelineAction}>{event.action.replace('_', ' ')}</span>
                  <span style={styles.timelineUser}>by {event.user}</span>
                  <span style={styles.timelineTime}>{new Date(event.timestamp).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'notes' && (
        <div style={styles.content} data-testid="notes-content">
          {workOrder.notes.length === 0 ? (
            <div style={styles.emptyState}>No notes recorded</div>
          ) : (
            <div style={styles.notesList}>
              {workOrder.notes.map(note => (
                <div key={note.id} style={styles.noteCard}>
                  <p style={styles.noteText}>{note.text}</p>
                  <div style={styles.noteMeta}>
                    <span>{note.author}</span>
                    <span>{new Date(note.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  backLink: { background: 'none', border: 'none', color: '#3182ce', fontSize: '0.875rem', cursor: 'pointer', padding: 0, marginBottom: '1rem', display: 'block' },
  backButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.875rem', cursor: 'pointer' },
  headerMeta: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  badge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  typeBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', background: '#e2e8f0', color: '#4a5568', textTransform: 'capitalize' },
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' },
  tab: { padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', background: 'transparent', fontSize: '0.875rem', cursor: 'pointer', color: '#718096' },
  activeTab: { padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', background: '#ebf8ff', fontSize: '0.875rem', cursor: 'pointer', color: '#3182ce' },
  content: { marginTop: '1rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1rem' },
  card: { background: 'white', borderRadius: '8px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem' },
  cardTitle: { margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' },
  field: { display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' },
  fieldLabel: { fontSize: '0.75rem', color: '#718096' },
  fieldValue: { fontSize: '0.875rem', fontWeight: '500', color: '#2d3748' },
  description: { margin: 0, fontSize: '0.875rem', color: '#4a5568', lineHeight: '1.5' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096', background: 'white', borderRadius: '8px' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  photosGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' },
  photoCard: { background: 'white', borderRadius: '8px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  photoPlaceholder: { fontSize: '3rem', textAlign: 'center', padding: '1rem', background: '#f7fafc', borderRadius: '4px', marginBottom: '0.5rem' },
  photoInfo: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  photoFilename: { fontSize: '0.75rem', fontWeight: '500', color: '#2d3748' },
  photoCaption: { fontSize: '0.75rem', color: '#718096' },
  photoDate: { fontSize: '0.625rem', color: '#a0aec0' },
  timeline: { position: 'relative', paddingLeft: '2rem' },
  timelineItem: { position: 'relative', paddingBottom: '1.5rem', borderLeft: '2px solid #e2e8f0', paddingLeft: '1.5rem', marginLeft: '-2rem' },
  timelineDot: { position: 'absolute', left: '-0.5rem', top: '0.25rem', width: '0.75rem', height: '0.75rem', borderRadius: '50%', background: '#3182ce', border: '2px solid white' },
  timelineContent: { display: 'flex', flexDirection: 'column', gap: '0.125rem' },
  timelineAction: { fontSize: '0.875rem', fontWeight: '500', color: '#2d3748', textTransform: 'capitalize' },
  timelineUser: { fontSize: '0.75rem', color: '#718096' },
  timelineTime: { fontSize: '0.75rem', color: '#a0aec0' },
  notesList: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  noteCard: { background: 'white', borderRadius: '8px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  noteText: { margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#2d3748' },
  noteMeta: { display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#718096' }
};
